import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, SectionHeader, Td, Th } from '@/components/erp/primitives'
import { OffsetPager } from '@/shared/ui/CursorPager'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import { Pill, fmtRelative } from '@/features/dossier/components/primitives'
import { listSubjects, type Subject } from '@/features/dossier/api'
import { askAboutSubject, listSubjectQuestions, listThreads,
         type CouncilRun, type Thread } from './api'
import { Metric, PlatformChip, SentimentChip, StatusChip, ThreatScore, Velocity } from './components'

/**
 * One subject, and everything being said about it.
 *
 * ## Why this screen exists separately from the feed
 *
 * The threads tab answers "what is happening"; this answers "what is happening TO HIM". A room
 * watches several subjects at once and the feed mixes them, so the judgement an operator
 * actually has to make — is this one angry account or a movement, is our line landing, should
 * we answer at all — was spread across fifty rows and had to be assembled in somebody's head.
 *
 * ## Asking is not drafting
 *
 * The council can already read one thread and draft replies to it. Both are fixed-shape
 * recipes that feed the approval queue. The question box here is free-form and feeds a
 * DECISION: its answer is prose, nothing it produces is publishable, and it needs no approval
 * gate because it cannot reach the public. Drafting a reply remains a separate act with the
 * gate it already has.
 *
 * ## The conversation stays on screen
 *
 * Every question and the answer that came back are listed together. A report that has lost the
 * question it answered is most of the way to useless, and an answer that lives only in the
 * advisory console is one nobody on shift will go and find.
 */
export function SubjectPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { authorities } = useSession()
  const canAsk = hasPermission(authorities, 'warroom:triage')

  const [subject, setSubject] = useState<Subject | null>(null)
  const [threads, setThreads] = useState<Thread[] | null>(null)
  const [runs, setRuns] = useState<CouncilRun[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [question, setQuestion] = useState('')
  const [page, setPage] = useState(0)
  const [total, setTotal] = useState({ pages: 1, elements: 0 })

  const load = useCallback(async () => {
    try {
      setError(null)
      const [found, asked] = await Promise.all([
        listThreads({ subjectId: id, page, size: 50 }),
        listSubjectQuestions(id),
      ])
      setThreads(found.content)
      setTotal({ pages: found.totalPages, elements: found.totalElements })
      setRuns(asked)
    } catch (err) {
      setError(explain(err))
    }
  }, [id, page])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    void listSubjects({ size: '200' })
      .then((all) => setSubject(all.find((s) => s.id === id) ?? null))
      .catch(() => setSubject(null))
  }, [id])

  /**
   * Poll while a question is in flight.
   *
   * A council run takes tens of seconds to minutes and the operator is looking at this screen
   * the whole time. Without this they would have to reload to find out it had landed, which is
   * how a feature gets a reputation for not working.
   */
  useEffect(() => {
    if (!runs.some((r) => r.status === 'PENDING')) return
    const timer = setInterval(() => void load(), 10_000)
    return () => clearInterval(timer)
  }, [runs, load])

  if (error && !threads) return <ErrorStrip message={error} />
  if (!threads) return <LoadingBlock label="Loading this subject" />

  async function ask(event: React.FormEvent) {
    event.preventDefault()
    if (question.trim().length < 10) return
    setBusy(true)
    try {
      await askAboutSubject(id, question.trim())
      setQuestion('')
      await load()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title={subject?.displayName ?? 'Subject'}
        description={`Every thread the room has found about this subject, and what the council
                      has been asked about them. ${total.elements} thread${
                        total.elements === 1 ? '' : 's'} captured.`}
      />

      {error && <ErrorStrip message={error} />}

      <Panel>
        <div className="space-y-2 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] font-medium">Ask the council</p>
            <span className="text-[10px] text-muted-foreground">
              Reads every thread, post and reply above. Publishes nothing.
            </span>
          </div>

          {canAsk && (
            <form onSubmit={ask} className="space-y-1.5">
              <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows={2}
                        placeholder="Is the fee claim spreading, or is it the same four accounts? Has our line landed?"
                        className="w-full rounded border border-input bg-background px-2 py-1
                                   text-[12px]" />
              <div className="flex flex-wrap items-center gap-2">
                <button type="submit" disabled={busy || question.trim().length < 10}
                        className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1
                                   text-[11px] text-primary-foreground disabled:opacity-50">
                  <Sparkles className="size-3" /> {busy ? 'Asking…' : 'Ask'}
                </button>
                <span className="text-[10px] text-muted-foreground">
                  A question, not a topic — “summarise” returns a summary of everything, which
                  is the one answer nobody needs.
                </span>
              </div>
            </form>
          )}

          {runs.length === 0 && (
            <p className="text-[11px] text-muted-foreground">
              Nothing asked yet.
            </p>
          )}

          <ul className="space-y-2">
            {runs.map((r) => (
              <li key={r.runId} className="rounded border border-border p-2">
                <p className="text-[12px] font-medium">{r.question ?? 'A question'}</p>
                <p className="mt-0.5 text-[10px] text-muted-foreground">
                  asked {fmtRelative(r.requestedAt)}
                </p>
                <div className="mt-1.5">
                  {r.status === 'PENDING' ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-primary">
                      <Sparkles className="size-3 animate-pulse" />
                      The council is reading everything about this subject…
                    </span>
                  ) : r.status === 'FAILED' ? (
                    <span className="text-[11px] text-rose-600">{r.note}</span>
                  ) : (
                    <p className="whitespace-pre-wrap text-[12px] leading-relaxed">{r.note}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Panel>

      <Panel>
        <ErpTable head={<>
          <Th>Thread</Th>
          <Th>Platform</Th>
          <Th>Threat</Th>
          <Th className="axis-hide-sm">Reach</Th>
          <Th className="axis-hide-sm">Seen</Th>
          <Th>Status</Th>
        </>}>
          {threads.length === 0 && (
            <EmptyRow colSpan={6}
                      message="Nothing found about this subject yet. Add a watch, or capture a
                               thread by hand." />
          )}
          {threads.map((t) => (
            <tr key={t.id} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/war-room/threads/${t.id}`)}>
              <Td>
                <span className="font-medium">{t.title ?? '(no text captured)'}</span>
                <span className="block text-[10px] text-muted-foreground">
                  {t.authorHandle ? `@${t.authorHandle}` : 'unknown author'}
                </span>
              </Td>
              <Td><PlatformChip code={t.platformCode} /></Td>
              <Td>
                <div className="flex items-center gap-1">
                  <ThreatScore score={t.threatScore} />
                  <SentimentChip code={t.sentimentCode} />
                </div>
              </Td>
              <Td className="axis-hide-sm">
                <div className="flex flex-wrap items-center gap-1.5">
                  <Velocity value={t.velocity} />
                  <Metric label="likes" value={t.likes} />
                  <Metric label="replies" value={t.replies} />
                  <Metric label="reposts" value={t.reposts} />
                </div>
              </Td>
              <Td className="axis-hide-sm">
                <span className="text-[10px] text-muted-foreground">
                  {fmtRelative(t.firstSeenAt)}
                </span>
              </Td>
              <Td><StatusChip code={t.statusCode} /></Td>
            </tr>
          ))}
        </ErpTable>

        <OffsetPager page={page} totalPages={total.pages} totalElements={total.elements}
                     onPage={setPage} loading={false} unit="threads" />
      </Panel>

      <Pill className="border-border">
        {subject?.displayName ?? 'This subject'} · {total.elements} threads
      </Pill>
    </div>
  )
}
